(() => {
  if (window.ErtenFullscreenMenuLoaded) return;
  window.ErtenFullscreenMenuLoaded = true;

  const desktopMedia = window.matchMedia('(min-width: 990px)');
  const searchTimers = new WeakMap();
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  let defaultViewTimer;

  const updateScrollHint = (secondary) => {
    if (!secondary) return;
    const canScroll = secondary.scrollHeight - secondary.clientHeight - secondary.scrollTop > 4;
    if (canScroll) secondary.setAttribute('data-can-scroll', 'true');
    else secondary.removeAttribute('data-can-scroll');
  };

  const updateAllScrollHints = (menu) => {
    menu.querySelectorAll('.erten-menu__secondary').forEach(updateScrollHint);
  };

  const updateSecondaryHeight = (menu) => {
    if (!desktopMedia.matches) {
      menu.style.removeProperty('--erten-secondary-height');
      return;
    }
    const secondary = menu.querySelector('.erten-menu__group[data-active] > .erten-menu__secondary');
    if (!secondary) return;
    menu.style.setProperty('--erten-secondary-height', `${Math.max(0, window.innerHeight - secondary.getBoundingClientRect().top)}px`);
    updateScrollHint(secondary);
  };

  const restoreMediaToStage = (menu) => {
    const stage = menu.querySelector('.erten-menu__stage');
    if (!stage) return;
    menu.querySelectorAll('[data-erten-media], [data-erten-block-media]').forEach((media) => {
      if (media.parentElement !== stage) stage.insertBefore(media, stage.querySelector('[data-erten-search-view]'));
    });
  };

  const mountActiveMedia = (menu, index) => {
    if (!desktopMedia.matches) return;
    restoreMediaToStage(menu);
    const mount = menu.querySelector(`[data-erten-media-mount="${index}"]`);
    if (!mount) return;
    const legacyMedia = menu.querySelector(`[data-erten-media="${index}"][data-visible="true"]`);
    const blockMedia = menu.querySelector('[data-erten-block-media][data-visible="true"]');
    if (legacyMedia?.querySelector('img')) mount.append(legacyMedia);
    if (blockMedia) mount.append(blockMedia);
  };

  const showEditorialCards = (menu, target) => {
    const cards = [...menu.querySelectorAll('[data-erten-editorial-card]')];
    let visibleCount = 0;
    cards.forEach((card) => {
      const visible = card.dataset.menuTarget === String(target) && visibleCount < 3;
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });
    const blockMedia = menu.querySelector('[data-erten-block-media]');
    if (blockMedia) {
      if (visibleCount) blockMedia.setAttribute('data-visible', 'true');
      else blockMedia.removeAttribute('data-visible');
    }
  };

  const showDefaultView = (menu) => {
    if (!menu || !desktopMedia.matches || menu.dataset.view === 'search') return;
    restoreMediaToStage(menu);
    menu.removeAttribute('data-active-index');
    menu.removeAttribute('data-pinned-index');
    menu.style.removeProperty('--erten-secondary-height');
    menu.querySelectorAll('.erten-menu__group[data-active]').forEach((group) => {
      group.removeAttribute('data-active');
      group.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
    menu.querySelectorAll('[data-erten-media][data-visible]').forEach((media) => media.removeAttribute('data-visible'));
    showEditorialCards(menu, 'default');
  };

  const syncGroupDisclosure = () => {
    document.querySelectorAll('[data-erten-menu] .erten-menu__group').forEach((group) => {
      if (desktopMedia.matches) {
        group.setAttribute('open', '');
      } else {
        const menu = group.closest('[data-erten-menu]');
        restoreMediaToStage(menu);
        menu?.removeAttribute('data-mobile-page');
        menu?.removeAttribute('data-active-index');
        group.removeAttribute('open');
        group.removeAttribute('data-mobile-active');
      }
      const secondary = group.querySelector(':scope > .erten-menu__secondary');
      if (secondary && !secondary.dataset.scrollBound) {
        secondary.dataset.scrollBound = 'true';
        secondary.addEventListener('scroll', () => updateScrollHint(secondary), { passive: true });
      }
    });
  };

  syncGroupDisclosure();
  desktopMedia.addEventListener('change', syncGroupDisclosure);

  const syncState = (menu) => {
    const isOpen = menu.open;
    const requestedView = menu.dataset.view;
    document.documentElement.classList.toggle('erten-menu-is-open', isOpen);
    menu.querySelector('.erten-menu__toggle')?.setAttribute('aria-expanded', String(isOpen));
    menu.querySelector('.erten-menu__toggle')?.setAttribute('aria-label', isOpen ? menu.dataset.closeLabel : menu.dataset.menuLabel);

    if (isOpen) {
      const isSearchView = requestedView === 'search';
      if (!desktopMedia.matches && !menu.dataset.mobilePage) {
        menu.querySelectorAll('.erten-menu__group').forEach((group) => {
          group.removeAttribute('open');
          group.removeAttribute('data-mobile-active');
        });
      }
      menu.removeAttribute('data-active-index');
      menu.removeAttribute('data-pinned-index');
      if (!isSearchView) menu.removeAttribute('data-view');
      menu.querySelectorAll('.erten-menu__group[data-active]').forEach((group) => group.removeAttribute('data-active'));
      menu.querySelectorAll('[data-erten-media][data-visible]').forEach((media) => media.removeAttribute('data-visible'));
      showEditorialCards(menu, isSearchView ? '__search__' : 'default');

      if (isSearchView) {
        window.setTimeout(() => menu.querySelector('#ErtenMenuSearch')?.focus(), 0);
      }
      window.requestAnimationFrame(() => updateAllScrollHints(menu));
    } else {
      menu.removeAttribute('data-active-index');
      menu.removeAttribute('data-view');
      menu.removeAttribute('data-pinned-index');
      menu.removeAttribute('data-mobile-page');
      menu.querySelectorAll('.erten-menu__group').forEach((group) => {
        group.removeAttribute('data-mobile-active');
        if (!desktopMedia.matches) group.removeAttribute('open');
      });
    }
  };

  const showMedia = (menu, index) => {
    menu.querySelectorAll('[data-erten-media]').forEach((media) => {
      if (media.dataset.ertenMedia === String(index)) media.setAttribute('data-visible', 'true');
      else media.removeAttribute('data-visible');
    });
    menu.setAttribute('data-active-index', String(index));
    menu.removeAttribute('data-view');
    showEditorialCards(menu, index);
    mountActiveMedia(menu, index);

    if (desktopMedia.matches) {
      window.requestAnimationFrame(() => {
        updateSecondaryHeight(menu);
      });
    }

  };

  const activateGroup = (item, pin = false) => {
    const menu = item.closest('[data-erten-menu]');
    const group = item.querySelector(':scope > .erten-menu__group');
    if (!menu || !desktopMedia.matches) return;
    if (!pin && menu.dataset.pinnedIndex && menu.dataset.pinnedIndex !== item.dataset.menuIndex) return;

    menu.querySelectorAll('.erten-menu__group[data-active]').forEach((activeGroup) => {
      activeGroup.removeAttribute('data-active');
      activeGroup.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
    group?.setAttribute('data-active', 'true');
    group?.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
    if (pin) menu.dataset.pinnedIndex = item.dataset.menuIndex;
    showMedia(menu, item.dataset.menuIndex);
  };

  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.('[data-erten-menu]')) syncState(event.target);
  }, true);

  document.addEventListener('keydown', (event) => {
    const menu = document.querySelector('[data-erten-menu][open]');
    if (!menu) return;
    if (event.key === 'Tab') {
      const focusable = [...menu.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }
    if (event.key !== 'Escape') return;
    menu.removeAttribute('open');
    syncState(menu);
    menu.querySelector('.erten-menu__toggle')?.focus();
  });

  document.addEventListener('pointerup', (event) => {
    const toggle = event.target.closest?.('.erten-menu__toggle');
    if (toggle && event.pointerType !== '') toggle.blur();
  });

  document.addEventListener('pointerover', (event) => {
    const panel = event.target.closest?.('[data-erten-menu] .erten-menu__panel');
    if (panel && desktopMedia.matches) {
      window.clearTimeout(defaultViewTimer);
      const isMenuInteraction = event.target.closest?.('.erten-menu__primary-link, .erten-menu__secondary');
      if (!isMenuInteraction) {
        const menu = panel.closest('[data-erten-menu]');
        defaultViewTimer = window.setTimeout(() => showDefaultView(menu), 180);
      }
    }
    const item = event.target.closest?.('.erten-menu__primary-item');
    if (item) activateGroup(item);
  });

  document.addEventListener('focusin', (event) => {
    const item = event.target.closest?.('.erten-menu__primary-item');
    if (item) activateGroup(item);
  });

  document.addEventListener('click', (event) => {
    const menuToggle = event.target.closest?.('[data-erten-menu] .erten-menu__toggle');
    if (menuToggle) {
      const menu = menuToggle.closest('[data-erten-menu]');
      if (menu?.dataset.view === 'search') {
        event.preventDefault();
        menu.removeAttribute('data-view');
        menu.removeAttribute('data-active-index');
        menu.removeAttribute('data-mobile-page');
        menu.querySelectorAll('.erten-menu__group[data-mobile-active]').forEach((group) => {
          group.removeAttribute('data-mobile-active');
          group.removeAttribute('open');
        });
        showEditorialCards(menu, 'default');
        syncState(menu);
        return;
      }
    }

    const searchTrigger = event.target.closest?.('[data-erten-search-trigger]');
    if (searchTrigger) {
      event.preventDefault();
      const menu = document.querySelector('[data-erten-menu]');
      if (!menu) return;
      menu.setAttribute('open', '');
      document.documentElement.classList.add('erten-menu-is-open');
      menu.setAttribute('data-view', 'search');
      menu.removeAttribute('data-active-index');
      menu.querySelectorAll('.erten-menu__group[data-active]').forEach((group) => group.removeAttribute('data-active'));
      menu.querySelectorAll('[data-erten-media][data-visible]').forEach((media) => media.removeAttribute('data-visible'));
      showEditorialCards(menu, '__search__');
      window.setTimeout(() => menu.querySelector('[data-erten-search-view] input')?.focus(), 0);
      return;
    }

    const groupSummary = event.target.closest?.('[data-erten-menu] .erten-menu__group > summary');
    if (groupSummary && desktopMedia.matches) {
      event.preventDefault();
      const item = groupSummary.closest('.erten-menu__primary-item');
      const menu = item.closest('[data-erten-menu]');
      activateGroup(item, true);
      menu.dataset.pinnedIndex = item.dataset.menuIndex;
      return;
    }
    if (groupSummary && !desktopMedia.matches) {
      event.preventDefault();
      const group = groupSummary.parentElement;
      const menu = group.closest('[data-erten-menu]');
      menu.querySelectorAll('.erten-menu__group[data-mobile-active]').forEach((active) => active.removeAttribute('data-mobile-active'));
      group.setAttribute('open', '');
      group.setAttribute('data-mobile-active', 'true');
      menu.setAttribute('data-mobile-page', group.dataset.menuIndex);
      showMedia(menu, group.dataset.menuIndex);
      menu.querySelector('.erten-menu__panel')?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const backButton = event.target.closest?.('[data-erten-menu-back]');
    if (backButton) {
      event.preventDefault();
      const menu = backButton.closest('[data-erten-menu]');
      menu.removeAttribute('data-mobile-page');
      menu.removeAttribute('data-active-index');
      menu.querySelectorAll('.erten-menu__group[data-mobile-active]').forEach((group) => {
        group.removeAttribute('data-mobile-active');
        group.removeAttribute('open');
      });
      menu.querySelectorAll('[data-erten-media][data-visible]').forEach((media) => media.removeAttribute('data-visible'));
      showEditorialCards(menu, 'default');
      return;
    }

    const link = event.target.closest?.('[data-erten-menu] a');
    if (!link) return;
    const menu = link.closest('[data-erten-menu]');
    menu.removeAttribute('open');
    syncState(menu);
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('#ErtenMenuSearch');
    if (!input) return;
    const menu = input.closest('[data-erten-menu]');
    const results = menu.querySelector('[data-erten-search-results]');
    const status = menu.querySelector('[data-erten-search-status]');
    const viewAll = menu.querySelector('[data-erten-search-all]');
    const query = input.value.trim();

    window.clearTimeout(searchTimers.get(input));
    results.replaceChildren();
    viewAll.hidden = true;
    if (query.length < 2) {
      status.textContent = '';
      return;
    }

    status.textContent = '…';
    searchTimers.set(input, window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product',
          'resources[limit]': '10',
          'resources[options][unavailable_products]': 'hide'
        });
        const response = await fetch(`${menu.dataset.predictiveSearchUrl}?${params.toString()}`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search request failed');
        const payload = await response.json();
        if (input.value.trim() !== query) return;
        const products = payload.resources?.results?.products || [];
        const fragment = document.createDocumentFragment();

        products.slice(0, 10).forEach((product) => {
          const card = document.createElement('a');
          card.className = 'erten-menu__search-card';
          card.href = product.url;
          const imageUrl = product.featured_image?.url || product.image;
          if (imageUrl) {
            const image = document.createElement('img');
            image.src = imageUrl;
            image.alt = product.featured_image?.alt || product.title || '';
            image.loading = 'lazy';
            card.append(image);
          }
          const title = document.createElement('span');
          title.textContent = product.title;
          card.append(title);
          if (Number.isFinite(Number(product.price))) {
            const price = document.createElement('span');
            price.className = 'erten-menu__search-price';
            const currency = menu.dataset.currency || 'TRY';
            const formatter = new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
            const format = value => currency === 'TRY' ? formatter.format(Number(value)) + ' TL' : new Intl.NumberFormat(document.documentElement.lang, {style: 'currency', currency}).format(Number(value));
            price.textContent = format(product.price);
            if (Number(product.compare_at_price_min) > Number(product.price)) {
              const original = document.createElement('s');
              original.textContent = format(product.compare_at_price_min);
              price.append(original);
            }
            card.append(price);
          }
          fragment.append(card);
        });
        results.replaceChildren(fragment);
        const words = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);
        const directMatch = products.some(product => words.every(word => product.title.toLocaleLowerCase().includes(word)));
        status.textContent = products.length ? (directMatch ? '' : menu.dataset.noTitleMatch || '') : menu.dataset.noResults || '';
        viewAll.href = `${menu.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`;
        viewAll.hidden = products.length === 0;
      } catch (_error) {
        if (input.value.trim() === query) status.textContent = menu.dataset.errorLabel || ''; 
      }
    }, 250));
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-erten-menu][open]').forEach((menu) => {
      updateSecondaryHeight(menu);
      updateAllScrollHints(menu);
    });
  });
})();
